import { Component, Input } from '@angular/core';
import { IMulta, IMultaMapped, IMultasDataSource, IReporteMultas, ITotalesDataSource } from 'src/app/models/direccion-trabajo/multas.model';
import { PdfGeneratorService } from 'src/app/shared/services/pdf-generator.service';
import { AlertService } from '../../_alert/alert.service';
import { goToTop } from 'src/app/shared/utils/utils';
import { EMultasStatus } from 'src/app/enum/EDireccionTrabajo';

@Component({
  selector: 'app-multas-viewer',
  templateUrl: './multas-viewer.component.html',
  styleUrls: ['./multas-viewer.component.scss']
})
export class MultasViewerComponent {
    @Input() content!: IReporteMultas;
    @Input() showHeader = true;

    readonly PDF_REPORT_NAME = 'reporteMultas';

    readonly STATUS_TX_LABELS: Record<EMultasStatus, string> = {
        [EMultasStatus.PAGADA]: 'Pagada',
        [EMultasStatus.EN_TRAMITE_DE_COBRANZA]: 'En trámite de cobranza',
    };

    private idTransaccion!: string;
    private multas: IMultaMapped[] = []
    
    public rut = '';
    public today = '';
    public razonSocial = '';

    public multasDataSource: IMultasDataSource[] = [];
    public multasDisplayedColumns: string[] = [
        'procedencia',
        'multa',
        'tipo',
        'estado',
        'fecha',
        'monto',
        'enunciado',
    ];

    totalMultasEjecutoriadasDataSource: ITotalesDataSource[] = [];
    totalMultasLey21227DataSource: ITotalesDataSource[] = [];
    totalMultasDisplayedColumns: string[] = ['unidad', 'pagadas', 'cobranza'];

    isPDFLoading = false;

    constructor(
        private pdfGeneratorService: PdfGeneratorService,
        private alertService: AlertService,
    ) {}

    ngOnInit() {
        this.setMultas();
        this.setRazonSocial();
        this.setMultasDataSource();
        this.setRut();
        this.setToday();

        this.totalMultasEjecutoriadasDataSource = this.setTotales(this.content.Reporte.report.multas);
        this.totalMultasLey21227DataSource = this.setTotales(this.content.Reporte.report.multas21227);

        this.idTransaccion = this.content.DatosBasicosSolicitud.IDTransaccion;
    }

    setRut() {
        this.rut = this.content.DatosBasicosSolicitud.Rut + '-' + this.content.DatosBasicosSolicitud.DV
    }

    setToday() {
        this.today = `${new Date().getFullYear()}${(new Date().getMonth() + 1 < 10 ? '0' + (new Date().getMonth() + 1) :
            new Date().getMonth() + 1)}${new Date().getDate()}`;
    }

    setRazonSocial() {
        this.razonSocial = this.multas[0]?.razonSocial;
    }

    setMultas() {
        this.multas = [
            ...(this.content.Reporte.report.multas ?? [])
                .map(multa => ({ ...multa, _tipoOrigen: 'Ejecutoriada' })),
            ...(this.content.Reporte.report.multas21227 ?? [])
                .map(multa => ({ ...multa, _tipoOrigen: 'Ley 21.227' })),
        ]
    }

    setTotales(multas: IMulta[]): ITotalesDataSource[] {
        const acumulado = multas.reduce<Record<string, ITotalesDataSource>>(
            (acc, multa) => {
                const unidad = multa.tipoUm;
                const monto = Number(multa.umFinal) || 0;

                const esCobranza =
                    multa.estado?.toUpperCase().includes('COBRANZA');

                acc[unidad] ??= {
                    unidad,
                    pagadas: { total: 0, cantidad: 0 },
                    cobranza: { total: 0, cantidad: 0 }
                };

                const bucket = esCobranza ? 'cobranza' : 'pagadas';

                acc[unidad][bucket].total += monto;
                acc[unidad][bucket].cantidad += 1;

                return acc;
            }, {}
        );

        return Object.values(acumulado);
    }

    formatMonto(total: number, cantidad: number): string {
        if (!total || cantidad === 0) {
            return '-';
        }

        const label = cantidad === 1 ? 'multa' : 'multas';

        return `${total.toFixed(2)} (${cantidad} ${label})`;
    }

    getDate(isoDate: string): string {
        const [year, month, day] = isoDate.split('T')[0].split('-');

        return `${day}-${month}-${year}`;
    }

    setMultasDataSource() {
        this.multasDataSource = this.multas.map(multa => ({
            procedencia: multa.procedencia,
            multa: multa.multa,
            tipo: multa._tipoOrigen,
            estado: multa.estado,
            fecha: this.getDate(multa.fechaEjecutariedad),
            monto: `${multa.umFinal} ${multa.tipoUm}`,
            enunciado: multa.enunciado
        }))
        .sort((a, b) => a.procedencia.localeCompare(b.procedencia));
    }

    getPDF() {
        this.isPDFLoading = true;
        this.pdfGeneratorService._getPDF(this.idTransaccion, this.PDF_REPORT_NAME).subscribe(
            response => {
                if (response.URLReporte) {
                    window.open(response.URLReporte, '_blank');
                }
                this.isPDFLoading = false;
            },
            ({error}) => {
                this.alertService.error(`Error al obtener el PDF: ${error?.message}`);
                goToTop();

                this.isPDFLoading = false;
            }
        )
    }

    generatePDF() {
        this.isPDFLoading = true;
        this.pdfGeneratorService._generatePDF(this.idTransaccion, this.PDF_REPORT_NAME, '', '', this.rut).subscribe(
            () => {
                this.getPDF();
            },
            ({error}) => {
                this.alertService.error(`Error al generar el PDF: ${error?.message}`);
                goToTop();

                this.isPDFLoading = false;
            }
        )
    }

    getStatusLabel(estado: string): string {
        return this.STATUS_TX_LABELS[estado as EMultasStatus] || estado;
    }

    getStatusClass(estado: string): string {
        return estado === EMultasStatus.PAGADA ? 'label-success' : 'label-warning';
    }
}
